import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO_BADGE = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/aj8j6qqi_Main.png";

const MAIN_LINKS = [
  { label: "Home", path: "/" },
  { label: "Experiences", path: "/events" },
  { label: "Sponsor", path: "/sponsor-veteran" },
  { label: "Episodes", path: "/episodes" },
  { label: "Fitness Camp", path: "/fitness-camp" },
];

const MORE_LINKS = [
  { label: "About", path: "/about" },
  { label: "Mission", path: "/mission" },
  { label: "Resources", path: "/resources" },
  { label: "Share", path: "/share" },
  { label: "Contact", path: "/contact" },
];

const ALL_LINKS = [...MAIN_LINKS, ...MORE_LINKS];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setMoreOpen(false); }, [location]);

  useEffect(() => {
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isMoreActive = MORE_LINKS.some(l => location.pathname === l.path);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0a1628]/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_BADGE} alt="Bad Company Veterans Foundation" className="h-14 w-14 object-contain" />
            <div className="hidden sm:block">
              <span className="font-heading text-lg uppercase tracking-wider block leading-tight">Bad Company</span>
              <span className="font-body text-[9px] uppercase tracking-[0.25em] text-brand-gold block">Veterans Foundation</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {MAIN_LINKS.map((link) => (
              <Link key={link.path} to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`font-subheading text-sm uppercase tracking-[0.15em] transition-colors duration-300 ${
                  location.pathname === link.path ? "text-white" : "text-slate-400 hover:text-white"
                }`}>
                {link.label}
              </Link>
            ))}
            <div ref={moreRef} className="relative">
              <button onClick={() => setMoreOpen(!moreOpen)} data-testid="nav-more-btn"
                className={`font-subheading text-sm uppercase tracking-[0.15em] transition-colors duration-300 flex items-center gap-1 ${
                  isMoreActive || moreOpen ? "text-white" : "text-slate-400 hover:text-white"
                }`}>
                More <ChevronDown size={14} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-[#0f1f35]/95 backdrop-blur-xl border border-white/10 py-2 shadow-2xl">
                  {MORE_LINKS.map((link) => (
                    <Link key={link.path} to={link.path}
                      className={`block px-5 py-2.5 font-subheading text-xs uppercase tracking-[0.15em] transition-colors ${
                        location.pathname === link.path ? "text-white bg-white/5" : "text-slate-400 hover:text-white hover:bg-white/5"
                      }`}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/get-involved" data-testid="nav-cta-get-involved"
              className="hidden sm:inline-block font-subheading text-sm uppercase tracking-widest btn-gold px-6 py-2.5 transition-colors duration-300">
              Get Involved
            </Link>
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0a1628]/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-8 space-y-5">
            {ALL_LINKS.map((link) => (
              <Link key={link.path} to={link.path}
                className={`block font-subheading text-lg uppercase tracking-wider ${
                  location.pathname === link.path ? "text-white" : "text-slate-500"
                }`}>
                {link.label}
              </Link>
            ))}
            <Link to="/get-involved" className="block font-subheading text-sm uppercase tracking-widest btn-gold px-6 py-3 text-center mt-4">
              Get Involved
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
